import React from 'react'
import PropTypes from 'prop-types';
import ImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/css/image-gallery.css';
import { makeStyles } from '@material-ui/core/styles';
import {
    Typography,
    Grid,
    Box,
    Paper,
    IconButton,
    Link
} from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import FacebookIcon from '@material-ui/icons/Facebook';
import TwitterIcon from '@material-ui/icons/Twitter';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import LanguageIcon from '@material-ui/icons/Language';

import { getResizedImageUrl } from '../../../utils/imageManager';
import JobDetailLocation from './JobDetailLocation';

const useStyles = makeStyles((theme) => ({
    companyInfo: {
        marginBottom: theme.spacing(3),
    },
    companyBody: {
        padding: theme.spacing(2),
        backgroundColor: '#fffceb',
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
    },
    companyName: {
        fontSize: '2.2rem',
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1.5),
        fontWeight: 600,
    },
    companyContainer: {
        marginTop: theme.spacing(2),
    },
    companyImages: {
        maxWidth: '60rem',
        margin: 'auto',
        marginBottom: theme.spacing(2),
    },
    companyDescription: {
        whiteSpace: 'pre-wrap',
        marginBottom: theme.spacing(2),
        lineHeight: 1.6,
    },
    websiteSection: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    websiteTitle: {
        fontSize: '1.6rem',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    websiteLink: {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        '&:hover': {
            textDecoration: 'none',
        },
    },
    socialLinks: {
        marginTop: theme.spacing(1),
    },
    locationIcon: {
        color: '#4a4a4a',
        fontSize: '1.2rem',
        marginRight: theme.spacing(1),
        verticalAlign: 'middle',
    },
    socialIcon: {
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    facebookIcon: {
        color: '#1877f2',
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    twitterIcon: {
        color: '#1da1f2',
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    linkedinIcon: {
        color: '#0077b5',
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    instagramIcon: {
        color: '#e4405f',
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    websiteIcon: {
        color: '#3273dc',
        fontSize: '2.4rem',
        '&:hover': {
            opacity: 0.8,
        },
    },
    iconButton: {
        padding: theme.spacing(1),
    },
    locationSection: {
        marginTop: theme.spacing(2),
    },
    jobDetailGallery: {
        '& .image-gallery-slide': {
            backgroundColor: '#0a0a0a',
            border: 'solid 1px #0a0a0a',
        },
        '& .image-gallery-image': {
            textAlign: 'center',
        },
        '& .image-gallery-image img': {
            margin: 'auto',
            objectFit: 'contain',
            width: '100%',
            height: '40rem',
            [theme.breakpoints.down('sm')]: {
                height: '35rem',
            },
        },
    },
}));

const JobDetailCompanyInfo = props => {
    const classes = useStyles();

    if (!props.user) return <Typography>No data</Typography>;
    const { name, profile } = props.user;

    const renderCompanyImages = images => {
        if (!images || images.length === 0) return null;

        let items = [];

        images.forEach(image => {
            items.push({
                original: image.image_url,
                thumbnail: getResizedImageUrl(image.image_url, 'c_thumb,w_200')
            })
        })

        return (
            <Box className={classes.jobDetailGallery}>
                <ImageGallery
                    items={items}
                    autoPlay={true}
                    slideInterval={6000}
                    showNav={false}
                    showFullscreenButton={false}
                    showPlayButton={false}
                />
            </Box>
        )
    }

    const renderSocialLinks = () => {
        const links = [];

        if (profile.companyWebsite) {
            links.push(
                <IconButton
                    key="website"
                    href={profile.companyWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.iconButton}
                >
                    <LanguageIcon className={classes.websiteIcon} />
                </IconButton>
            );
        }

        if (profile.facebook) {
            links.push(
                <IconButton
                    key="facebook"
                    href={profile.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.iconButton}
                >
                    <FacebookIcon className={classes.facebookIcon} />
                </IconButton>
            );
        }

        if (profile.twitter) {
            links.push(
                <IconButton
                    key="twitter"
                    href={profile.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.iconButton}
                >
                    <TwitterIcon className={classes.twitterIcon} />
                </IconButton>
            );
        }

        if (profile.linkedin) {
            links.push(
                <IconButton
                    key="linkedin"
                    href={profile.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.iconButton}
                >
                    <LinkedInIcon className={classes.linkedinIcon} />
                </IconButton>
            );
        }

        if (profile.instagram) {
            links.push(
                <IconButton
                    key="instagram"
                    href={profile.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.iconButton}
                >
                    <InstagramIcon className={classes.instagramIcon} />
                </IconButton>
            );
        }

        return links;
    };

    return (
        <Box className={classes.companyInfo}>
            <Typography variant="h3" className="section-title" style={{ marginBottom: '2rem' }}>
                投稿者情報
            </Typography>
            <Paper className={classes.companyBody}>
                <Typography variant="h4" className={classes.companyName}>
                    {name}
                </Typography>
                <Box className={classes.companyContainer}>
                    <Grid container spacing={3}>
                        {profile.images && profile.images.length > 0 && (
                            <Grid item xs={12}>
                                <Box className={classes.companyImages}>
                                    {renderCompanyImages(profile.images)}
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography className={classes.companyDescription}>
                                {profile.introduction}
                            </Typography>
                        </Grid>
                        {profile.images && profile.images.length > 0 && (
                            <Grid item xs={12}>
                                <Box>
                                    {profile.companyWebsite && (
                                        <Box className={classes.websiteSection}>
                                            <Typography variant="h5" className={classes.websiteTitle}>
                                                ウェブサイト
                                            </Typography>
                                            <Box className={classes.socialLinks}>
                                                {renderSocialLinks()}
                                            </Box>
                                        </Box>
                                    )}
                                    {profile.address && (
                                        <Box className={classes.locationSection}>
                                            <JobDetailLocation address={profile.address} />
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        )}
                        {(!profile.images || profile.images.length === 0) && (
                            <Grid item xs={12}>
                                <Box>
                                    {profile.companyWebsite && (
                                        <Box className={classes.websiteSection}>
                                            <Typography variant="h5" className={classes.websiteTitle}>
                                                ウェブサイト
                                            </Typography>
                                            <Link
                                                href={profile.companyWebsite}
                                                className={classes.websiteLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {profile.companyWebsite}
                                            </Link>
                                            <Box className={classes.socialLinks}>
                                                {renderSocialLinks()}
                                            </Box>
                                        </Box>
                                    )}
                                    {profile.address && (
                                        <Box className={classes.locationSection}>
                                            <JobDetailLocation address={profile.address} />
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Paper>
        </Box>
    )
}

JobDetailCompanyInfo.propTypes = {
    user: PropTypes.object
}

export default JobDetailCompanyInfo
